import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ExamPage from "./components/ExamPage";
import ExamLoader from "./components/ExamLoader";
import { detectBrowser } from "./utils/browser";
import { getBrand } from "./branding";

const brand = getBrand();

export default function App({
  mode = "edit",
  practiceMode = false,
  chairMode = false,
  devMode = false
}) {
  const { examId } = useParams();

  const isIsmta =
    brand.shortName === "ISMTA";

  const chairExamAllowed =
    !chairMode ||
    !isIsmta ||
    examId === "level5aim";

  const chairSessionKey =
    `${brand.shortName}-chairAuthed`;

  const [exam, setExam] =
    useState(null);

  const [error, setError] =
    useState(null);

  const [blockedBrowser, setBlockedBrowser] =
    useState(null);

  const [delayDone, setDelayDone] =
    useState(true);

  const [chairAuthed, setChairAuthed] =
    useState(
      () =>
        sessionStorage.getItem(
          chairSessionKey
        ) === "true"
    );

  /* ───────── BROWSER CHECK (ON LOAD) ───────── */
  useEffect(() => {
    const browser = detectBrowser();

    if (
      browser !== "Chrome" &&
      browser !== "Firefox" &&
      browser !== "Safari"
    ) {
      setBlockedBrowser(browser);
    }
  }, []);

  /* ───────── FORCED MIN LOADING TIME ───────── */
  useEffect(() => {
    const t = setTimeout(
      () => setDelayDone(true),
      1200
    );

    return () => clearTimeout(t);
  }, []);

  /* ───────── LOAD EXAM ───────── */
  useEffect(() => {
    if (
      !examId ||
      blockedBrowser ||
      !chairExamAllowed
    ) {
      return;
    }

    setExam(null);
    setError(null);

    fetch(
      `/api/assessment/${examId}${
        practiceMode
          ? "?variant=practice"
          : ""
      }`
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(
            "Exam not found"
          );
        }

        return res.json();
      })
      .then(setExam)
      .catch((err) => {
        console.error(err);
        setError("Invalid exam");
      });
  }, [
    examId,
    blockedBrowser,
    practiceMode,
    chairExamAllowed
  ]);

  if (!chairExamAllowed) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "#f6f7f9"
        }}
      >
        <div
          style={{
            maxWidth: 520,
            padding: 32,
            background: "#fff",
            borderRadius: 12,
            textAlign: "center",
            boxShadow:
              "0 8px 24px rgba(0,0,0,0.1)"
          }}
        >
          <h2 style={{ marginTop: 0 }}>
            Exam Not Available
          </h2>

          <p>
            ISMTA chair access is currently
            limited to the Level 5 AIM exam.
          </p>

          <a href="/chair/private/level5aim">
            Open Level 5 AIM
          </a>
        </div>
      </div>
    );
  }

  if (chairMode && !chairAuthed) {
    return (
      <ChairPasswordGate
        onSuccess={() => {
          sessionStorage.setItem(
            chairSessionKey,
            "true"
          );

          setChairAuthed(true);
        }}
      />
    );
  }

  /* ───────── BLOCKED BROWSER ───────── */
  if (blockedBrowser) {
    return (
      <BrowserWarningModal
        browser={blockedBrowser}
      />
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!exam || !delayDone) {
    return (
      <ExamLoader
        message="Loading your exam…"
      />
    );
  }

  return (
    <ExamPage
      exam={exam}
      examId={examId}
      initialMode={mode}
      practiceMode={practiceMode}
      chairMode={chairMode}
      devMode={devMode}
    />
  );
}

function BrowserWarningModal({
  browser
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          maxWidth: 420,
          padding: 24,
          textAlign: "center",
          boxShadow:
            "0 10px 30px rgba(0,0,0,0.25)"
        }}
      >
        <h2 style={{ marginTop: 0 }}>
          Unsupported Browser
        </h2>

        <p style={{ fontSize: 16 }}>
          We see you are using{" "}
          <strong>{browser}</strong>.
        </p>

        <p>
          For reliability during exams and
          ear-training playback, please switch
          to one of the supported browsers:
        </p>

        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center"
          }}
        >
          <a
            href="https://www.google.com/chrome/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Chrome
          </a>

          <a
            href="https://www.mozilla.org/firefox/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Firefox
          </a>
        </div>

        <p
          style={{
            marginTop: 16,
            fontSize: 13,
            color: "#666"
          }}
        >
          This page will remain unavailable
          until you switch browsers.
        </p>
      </div>
    </div>
  );
}

function ChairPasswordGate({
  onSuccess
}) {
  const [pw, setPw] =
    useState("");

  const [error, setError] =
    useState(false);

  function submit(e) {
    e.preventDefault();

    const expectedPassword =
      brand.shortName === "ISMTA"
        ? "ISMTA-2026-CHAIR"
        : "TMTA-2026-CHAIR";

    if (pw === expectedPassword) {
      onSuccess();
      return;
    }

    setError(true);
    setPw("");
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: "#fff",
          padding: "32px",
          borderRadius: 12,
          width: 420,
          maxWidth: "90%",
          boxShadow:
            "0 20px 40px rgba(0,0,0,0.35)"
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: 8
          }}
        >
          {brand.shortName} Chair Access
        </h2>

        <p
          style={{
            fontSize: 14,
            color: "#444",
            marginBottom: 20
          }}
        >
          This page provides access to a live
          exam session for authorized{" "}
          {brand.shortName} officers.
        </p>

        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 6
          }}
        >
          Chair Password
        </label>

        <input
          type="password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setError(false);
          }}
          autoFocus
          style={{
            padding: "10px 12px",
            fontSize: 15,
            borderRadius: 6,
            border: "1px solid #ccc"
          }}
        />

        {error && (
          <div
            style={{
              marginTop: 10,
              fontSize: 13,
              color: "#b71c1c"
            }}
          >
            Incorrect password. Please try
            again.
          </div>
        )}

        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "left"
          }}
        >
          <button
            type="submit"
            style={{
              background: "#2e7d32",
              color: "#fff",
              border: "none",
              padding: "10px 18px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Enter Chair View
          </button>
        </div>
      </form>
    </div>
  );
}
